import { CalendarGame } from '../interfaces/calendar';
import { ChampionshipFilterItem, ChampionshipFilterOption } from '../interfaces/championship';

/** Площадка в начале названия: «Крылова 20. Ветеранская Елига | …» → «Крылова 20» и остальное название. */
const VENUE_SEPARATOR = /\s*\|\s*|\.\s+/;
const MAX_VENUE_LENGTH = 30;

export interface ChampionshipNameParts {
  /** Название без площадки; без разделителя — оно же целиком. */
  title: string;
  /** Что стоит до первого `|` или `. ` (точка с пробелом: десятичная точка названия не режет). */
  venue?: string;
}

export function splitChampionshipName(name: string): ChampionshipNameParts {
  const match = VENUE_SEPARATOR.exec(name);
  if (!match) return { title: name };

  const venue = name.slice(0, match.index).trim();
  const title = name.slice(match.index + match[0].length).trim();
  // Длинное начало — это уже название, а не площадка; пустой остаток — делить нечего.
  if (!venue || !title || venue.length > MAX_VENUE_LENGTH) return { title: name };

  return { title, venue };
}

/**
 * Варианты фильтра: турниры показанных месяцев с числом их матчей и — всегда — уже выбранные, даже когда в показанных
 * месяцах у них матчей нет (число 0): иначе выбранный турнир нельзя снять, пролистав на другой месяц.
 */
export function buildChampionshipOptions(matches: CalendarGame[], selected: ChampionshipFilterItem[]): ChampionshipFilterOption[] {
  const byId = new Map<number, ChampionshipFilterOption>();

  for (const { championship } of matches) {
    const option = byId.get(championship.id) ?? { id: championship.id, name: championship.name, count: 0 };
    option.count += 1;
    byId.set(championship.id, option);
  }

  for (const item of selected) {
    if (!byId.has(item.id)) byId.set(item.id, { ...item, count: 0 });
  }

  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
}

/** Матчи по дням только выбранных турниров; пустой фильтр — все матчи как есть. Дни без оставшихся матчей уходят. */
export function filterMatchesByChampionships(
  matchesByDay: Record<string, CalendarGame[]>,
  selected: ChampionshipFilterItem[],
): Record<string, CalendarGame[]> {
  if (selected.length === 0) return matchesByDay;

  const ids = new Set(selected.map(({ id }) => id));
  const filtered: Record<string, CalendarGame[]> = {};

  for (const [day, games] of Object.entries(matchesByDay)) {
    const kept = games.filter((game) => ids.has(game.championship.id));
    if (kept.length > 0) filtered[day] = kept;
  }

  return filtered;
}
